import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getEmailAccountStatus } from "../utils";
import CreateAccountForm from "./CreateAccountForm";
import CloseAccountModal from "./CloseAccountModal";

const Accounts = ({ isAdmin, loggedInUser, onSignOut }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [activeClosureRequest, setActiveClosureRequest] = useState(null);
  const [closureSuccessMessage, setClosureSuccessMessage] = useState(null);
  const [activeCreationRequest, setActiveCreationRequest] = useState(null);
  const [closedAccountRequest, setClosedAccountRequest] = useState(null);
  const [failedAccountRequest, setFailedAccountRequest] = useState(null);
  const [showFailedAccountCreateForm, setShowFailedAccountCreateForm] = useState(false);
  const lastFetchRequestIdRef = useRef(0);


  // Prevent cross-session UI bleed: when the authenticated user changes within
  // the same tab, clear any previously loaded account records immediately so
  // we never render the prior user's dashboard while the new fetch is in-flight.
  const resolveUserMarker = (user) => {
    const marker =
      user?.attributes?.sub ||
      user?.signInUserSession?.idToken?.payload?.sub ||
      user?.attributes?.email ||
      user?.signInUserSession?.idToken?.payload?.email ||
      user?.username ||
      user?.attributes?.preferred_username ||
      '';
    return String(marker || '').trim().toLowerCase();
  };

  const userMarker = resolveUserMarker(loggedInUser);

  useEffect(() => {
    // Invalidate any in-flight fetch from the previous identity.
    lastFetchRequestIdRef.current += 1;
    setAccounts([]);
    setActiveClosureRequest(null);
    setActiveCreationRequest(null);
    setClosedAccountRequest(null);
    setFailedAccountRequest(null);
    setShowFailedAccountCreateForm(false);
    setLoading(false);
  }, [userMarker]);

  useEffect(() => {
    const routedFailedAccount = location.state?.failedAccount || null;

    if (!routedFailedAccount) {
      return;
    }

    setFailedAccountRequest((currentFailedAccount) => {
      if (currentFailedAccount?.request_id === routedFailedAccount.request_id) {
        return currentFailedAccount;
      }

      return routedFailedAccount;
    });
  }, [location.state]);


  const fetchAccountStatusList = useCallback(async () => {
    const requestId = ++lastFetchRequestIdRef.current;

    // Use the same multi-source email resolution as getEmailAccountStatus so
    // we never bail early on a fresh-browser session where attributes.email
    // hasn't been hydrated yet but username or idToken email is available.
    const resolveAvailableEmail = (user) => {
      const candidates = [
        user?.attributes?.email,
        user?.signInUserSession?.idToken?.payload?.email,
        user?.username,
        user?.attributes?.preferred_username,
      ];
      for (const value of candidates) {
        const email = String(value || '').trim().toLowerCase();
        if (email && email.includes('@')) return email;
      }
      return '';
    };

    if (!resolveAvailableEmail(loggedInUser)) {
      setAccounts([]);
      setActiveClosureRequest(null);
      setActiveCreationRequest(null);
      setClosedAccountRequest(null);
      setFailedAccountRequest(null);
      setShowFailedAccountCreateForm(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const accountList = await getEmailAccountStatus(loggedInUser);
      const closureStatuses = [
        'CLOSURE_PENDING', 
        'DELETING_DASHBOARD', 
        'DELETING_INFRASTRUCTURE', 
        'ACCOUNT_CLOSED', 
        'DASHBOARD_DELETION_FAILED', 
        'INFRASTRUCTURE_DELETION_FAILED', 
        'IMAGE_PERMISSION_DELETION_FAILED',
        'STACKSET_DELETION_FAILED',
        'CLOSURE_FAILED', 
        'CLOSURE_VALIDATION_FAILED'
      ];
      
      const creationTerminalStatuses = [
        'COMPLETED',
        'FAILED',
        'REJECTED',
        'ACCOUNT_CREATION_REJECTED',
        'TIMED_OUT',
        'CANCELLED',
        'CANCELED',
        'DEPLOYMENT_CANCELLED',
        'DEPLOYMENT_CANCELED',
        'FAILED_IMAGE_SHARING',
        'FAILED_INFRASTRUCTURE_DEPLOYMENT',
        'FAILED_DASHBOARD_DEPLOYMENT',
        'INFRASTRUCTURE_CREATION_FAILED',
        'DASHBOARD_CREATION_FAILED'
      ];

      const failedCreationStatuses = [
        'FAILED',
        'TIMED_OUT',
        'FAILED_IMAGE_SHARING',
        'FAILED_INFRASTRUCTURE_DEPLOYMENT',
        'FAILED_DASHBOARD_DEPLOYMENT',
        'INFRASTRUCTURE_CREATION_FAILED',
        'DASHBOARD_CREATION_FAILED'
      ];

      const isTerminalCreationStatus = (status) => {
        const s = String(status || '').toUpperCase();
        if (!s) return false;
        if (creationTerminalStatuses.includes(s)) return true;
        // Be defensive: new backend statuses may be introduced without
        // updating the frontend list. Treat common terminal patterns as
        // terminal so old cancelled/failed requests don't appear "active".
        if (s.startsWith('FAILED')) return true;
        if (s.includes('CANCEL')) return true;
        if (s.includes('REJECT')) return true;
        if (s.includes('TIMED_OUT') || s.includes('TIMEOUT')) return true;
        return false;
      };

      const normalizedAccounts = (accountList || [])
        .map((account) => {
          const normalizedStatus = account.request_status || account.status || "";
          return {
            ...account,
            request_status: normalizedStatus,
          };
        })
        // Filter out closure statuses - only show account creation requests
        .filter((account) => {
          const status = (account.request_status || "").toUpperCase();
          return !closureStatuses.includes(status);
        })
        .sort((a, b) => {
          const parseTime = (value) => {
            if (!value) {
              return 0;
            }
            const time = Date.parse(value);
            return Number.isNaN(time) ? 0 : time;
          };
          const bTime = parseTime(b.updated_at || b.created_at);
          const aTime = parseTime(a.updated_at || a.created_at);
          return bTime - aTime;
        });

      if (requestId !== lastFetchRequestIdRef.current) {
        return;
      }

      // Fallback: if ALL records are rejected and none are approved/completed,
      // navigate to the rejection dashboard. This catches the case where the
      // App-level checkAccountStatus missed the redirect (e.g., due to auth
      // warm-up returning null on first call).
      if (!isAdmin) {
        // NOTE: Rejection routing is now handled by App.js checkAccountStatus()
        // as the single source of truth for all dashboard routing decisions.

        // NOTE: Incomplete deployment routing is now handled by App.js checkAccountStatus()
        // as the single source of truth for all dashboard routing decisions.

        // NOTE: Closed account routing is now handled by App.js checkAccountStatus()
        // as the single source of truth for all dashboard routing decisions.
        // The closed account dashboard is accessible at /closed route.
      }

      setAccounts(normalizedAccounts);
      
      const detectedClosure = (accountList || []).find((account) => {
        const status = (account.request_status || account.status || '').toUpperCase();
        const terminalClosureStatuses = [
          'ACCOUNT_CLOSED', 
          'DASHBOARD_DELETION_FAILED', 
          'INFRASTRUCTURE_DELETION_FAILED', 
          'IMAGE_PERMISSION_DELETION_FAILED',
          'STACKSET_DELETION_FAILED',
          'CLOSURE_FAILED', 
          'CLOSURE_VALIDATION_FAILED',
          'PENDING_SETTLEMENT',
          'ARCHIVE_MIGRATION_FAILED'
        ];
        return closureStatuses.includes(status) && !terminalClosureStatuses.includes(status);
      });
      setActiveClosureRequest(detectedClosure || null);
      
      const detectedCreation = normalizedAccounts.find((account) => {
         const status = (account.request_status || '').toUpperCase();
        return !isTerminalCreationStatus(status) && status !== 'APPROVED' && status !== 'COMPLETED';
      });
      // 'APPROVED' is technically active, but we handle 'approvedUser' separately for the dashboard view.
      // However, if we have an APPROVED user who is not yet COMPLETED (e.g. PENDING_APPROVAL -> APPROVED),
      // 'approvedUser' logic handles them?
      // approvedUser logic: status === "APPROVED" || status === "COMPLETED".
      // So 'detectedCreation' should exclude APPROVED.
      
      setActiveCreationRequest(detectedCreation || null);

      const detectedFailed = normalizedAccounts.find((account) => {
        const status = (account.request_status || '').toUpperCase();
        return failedCreationStatuses.includes(status);
      });

      // By default, detect a closed account in the raw list. We'll clear it
      // below if the user also has any active/approved creation requests so that
      // stale closures (e.g. account 399) don't linger when a newer account 400
      // exists.
      const detectedClosed = (accountList || []).find((account) => {
        const status = (account.request_status || account.status || '').toUpperCase();
        return status === 'ACCOUNT_CLOSED';
      });

      // Determine if there are any non-terminal creation records or an approved
      // account. If so, ignore the closed-account banner.
      const hasActiveCreation = normalizedAccounts.some((acc) => {
        const s = (acc.request_status || '').toUpperCase();
        return !creationTerminalStatuses.includes(s) && s !== 'APPROVED' && s !== 'COMPLETED';
      });

      const hasApproved = normalizedAccounts.some((account) => {
        const s = (account.request_status || '').toUpperCase();
        return s === 'APPROVED' || s === 'COMPLETED';
      });

      if (hasActiveCreation) {
        setClosedAccountRequest(null);
      } else {
        setClosedAccountRequest(detectedClosed || null);
      }

      if (hasApproved || hasActiveCreation || detectedClosure || detectedClosed) {
        setFailedAccountRequest(null);
        setShowFailedAccountCreateForm(false);
      } else {
        setFailedAccountRequest(detectedFailed || null);
      }
    } catch (error) {
      if (error?.name === 'AuthSessionInvalidError') {
        onSignOut?.();
        return;
      }
      if (requestId !== lastFetchRequestIdRef.current) {
        return;
      }
      console.error("Failed to load account status list", error);
    } finally {
      if (requestId === lastFetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser, location, navigate]);
  useEffect(() => {
    fetchAccountStatusList();
  }, [fetchAccountStatusList, loggedInUser]);
  
  // Find approved/completed user - check for both APPROVED and COMPLETED status
  const getNormalizedStatus = (account) => (account?.request_status || account?.status || "").toUpperCase();

  // Parse created_at regardless of whether it was stored as an ISO-8601 string
  // (current Lambda: new Date().toISOString()) OR as a numeric Unix ms timestamp
  // (older Lambda versions: Date.now()). Returns 0 when unparseable so the
  // request_id fallback below takes over.
  const parseCreatedAt = (v) => {
    if (!v) return 0;
    // Try ISO string first (handles the common case)
    const t = Date.parse(String(v));
    if (!isNaN(t)) return t;
    // Try treating as a raw numeric timestamp (seconds or milliseconds)
    const n = Number(v);
    if (!isNaN(n) && n > 0) return n < 1e12 ? n * 1000 : n;
    return 0;
  };

  // Extract the millisecond-epoch timestamp embedded in request_id (format: req-{ms}-{random}).
  // Used as a tiebreaker when created_at is missing or unparseable for both accounts.
  const extractRequestIdTimestamp = (requestId) => {
    const match = String(requestId || '').match(/^req-(\d+)-/);
    return match ? parseInt(match[1], 10) : 0;
  };

  // When multiple COMPLETED/APPROVED accounts exist (e.g. an old cancelled account #399
  // and the active account #400), always prefer the most recently CREATED one.
  // Primary sort: created_at (handles both ISO string and numeric Unix formats).
  // Secondary sort: request_id embedded timestamp (guards against missing created_at).
  const approvedUser = [...accounts]
    .sort((a, b) => {
      const timeA = parseCreatedAt(a.created_at) || extractRequestIdTimestamp(a.request_id);
      const timeB = parseCreatedAt(b.created_at) || extractRequestIdTimestamp(b.request_id);
      return timeB - timeA;
    })
    .find((account) => {
      const status = getNormalizedStatus(account);
      return status === "APPROVED" || status === "COMPLETED";
    });

  // Diagnostic: log the selected approvedUser so we can verify the correct
  // account is chosen when multiple COMPLETED records exist (e.g. 399 vs 400).
  if (process.env.NODE_ENV !== 'production') {
    const candidates = accounts.filter(a => { const s = getNormalizedStatus(a); return s === 'APPROVED' || s === 'COMPLETED'; });
    console.log('Accounts.js: approvedUser candidates:', candidates.map(a => ({
      account_email: a.account_email,
      company_name: a.company_name,
      status: a.request_status || a.status,
      created_at: a.created_at,
      request_id: a.request_id,
      parsed_time: parseCreatedAt(a.created_at) || extractRequestIdTimestamp(a.request_id),
    })));
    console.log('Accounts.js: selected approvedUser:', approvedUser
      ? { account_email: approvedUser.account_email, company_name: approvedUser.company_name, status: approvedUser.request_status }
      : null);
  }

  const closedAccount = closedAccountRequest;
  const failedAccount = failedAccountRequest;

  const handleCloseAccount = () => {
    setShowCloseModal(true);
  };

  const navigateToClosureStatus = useCallback((account) => {
    if (!account?.account_email || !account?.company_name) {
      return;
    }
    const normalizedStatus = (account.request_status || account.status || '').toUpperCase();
    navigate(
      `/status/${encodeURIComponent(account.account_email)}/${encodeURIComponent(account.company_name)}`,
      {
        state: {
          returnToClosedDashboard: normalizedStatus === 'ACCOUNT_CLOSED',
          pendingClosureNavigation: normalizedStatus === 'CLOSURE_PENDING',
          accountData: account,
          activityType: 'closure'
        }
      }
    );
  }, [navigate]);

  const navigateToStatus = useCallback((account, activityType = 'creation') => {
    if (!account?.account_email || !account?.company_name) {
      return;
    }
    const encodedEmail = encodeURIComponent(account.account_email);
    const encodedCompanyName = encodeURIComponent(account.company_name);
    navigate(
        `/status/${encodedEmail}/${encodedCompanyName}`,
        { state: { activityType } }
      );
  }, [navigate]);

  useEffect(() => {
    if (!failedAccount) {
      setShowFailedAccountCreateForm(false);
    }
  }, [failedAccount]);

  const failedStatusMessage = (() => {
    const status = (failedAccount?.request_status || failedAccount?.status || '').toUpperCase();

    switch (status) {
      case 'FAILED':
        return 'Account creation failed before deployment could complete.';
      case 'TIMED_OUT':
        return 'The account request timed out before deployment completed.';
      case 'FAILED_IMAGE_SHARING':
        return 'Account creation stopped while sharing AppStream images.';
      case 'FAILED_INFRASTRUCTURE_DEPLOYMENT':
      case 'INFRASTRUCTURE_CREATION_FAILED':
        return 'Infrastructure deployment failed for this account request.';
      case 'FAILED_DASHBOARD_DEPLOYMENT':
      case 'DASHBOARD_CREATION_FAILED':
        return 'Dashboard deployment failed for this account request.';
      default:
        return status ? `Account creation failed with status ${status.replace(/_/g, ' ')}.` : 'Account creation failed.';
    }
  })();

  const handleClosureComplete = useCallback((closureContext) => {
    const email = closureContext?.account_email || loggedInUser?.attributes?.email;
    const companyName =
      closureContext?.company_name ||
      activeClosureRequest?.company_name ||
      approvedUser?.company_name;

    fetchAccountStatusList();

    if (email && companyName) {
      setTimeout(() => {
        setShowCloseModal(false);
        setClosureSuccessMessage(null);
        navigateToClosureStatus({
          ...closureContext,
          account_email: email,
          company_name: companyName,
          account_number: closureContext?.account_number,
          name_prefix: closureContext?.name_prefix || approvedUser?.name_prefix,
          closure_reason: closureContext?.closure_reason,
          requester_email: closureContext?.requester_email || loggedInUser?.attributes?.email,
          request_status: 'CLOSURE_PENDING',
          created_at: closureContext?.created_at || approvedUser?.created_at,
          updated_at: closureContext?.updated_at || approvedUser?.updated_at
        });
        setClosureSuccessMessage(null);
      }, 1000);
    }
  }, [activeClosureRequest, approvedUser, loggedInUser, navigateToClosureStatus, fetchAccountStatusList]);
  
  return (
    <div className="nav-content">
      {closureSuccessMessage && (
        <div style={{
          position: "fixed",
          top: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10000,
          padding: "15px 30px",
          backgroundColor: "#d4edda",
          color: "#155724",
          border: "1px solid #c3e6cb",
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          fontWeight: "bold",
          textAlign: "center"
        }}>
          {closureSuccessMessage}
        </div>
      )}
      {loading ? (
        <div className="loading-overlay">
          <span className="loader"></span>
        </div>
      ) : (
        <>
          {activeClosureRequest && (
            <div style={{ marginBottom: "20px", padding: "20px", backgroundColor: "#fff3cd", borderRadius: "8px", border: "1px solid #ffc107" }}>
              <h3 style={{ color: "#856404", marginTop: 0 }}>Account Closure In Progress</h3>
              <p style={{ color: "#856404", marginBottom: "15px" }}>
                Current status: {(activeClosureRequest.request_status || activeClosureRequest.status || '').replace(/_/g, ' ')}.
              </p>
              <button
                onClick={() => navigateToClosureStatus(activeClosureRequest)}
                style={{
                  backgroundColor: "#dc3545",
                  color: "white",
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "background-color 0.2s"
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#c82333"}
                onMouseOut={(e) => e.target.style.backgroundColor = "#dc3545"}
              >
                View Closure Status
              </button>
            </div>
          )}
          {activeCreationRequest && (
            <div style={{ marginBottom: "20px", padding: "20px", backgroundColor: "#e3f2fd", borderRadius: "8px", border: "1px solid #2196f3" }}>
              <h3 style={{ color: "#0d47a1", marginTop: 0 }}>Account Request In Progress</h3>
              <p style={{ color: "#0d47a1", marginBottom: "15px" }}>
                Current status: {(activeCreationRequest.request_status || '').replace(/_/g, ' ')}.
              </p>
              <button
                onClick={() => navigateToStatus(activeCreationRequest)}
                style={{
                  backgroundColor: "#1976d2",
                  color: "white",
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "background-color 0.2s"
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#1565c0"}
                onMouseOut={(e) => e.target.style.backgroundColor = "#1976d2"}
              >
                View Request Status
              </button>
            </div>
          )}
          {!isAdmin && approvedUser ? (
            <div className="dashboard-container">
              <h2>Dashboard</h2>
              <div className="dashboard-info">
                <div className="dashboard-row">
                  <span className="dashboard-label">Account Number:</span>
                  <span className="dashboard-value">
                    {`******${String(approvedUser.account_number || '').slice(-6)}`}
                  </span>
                </div>
                <div className="dashboard-row">
                  <span className="dashboard-label">Name Prefix:</span>
                  <span className="dashboard-value">{approvedUser.name_prefix}</span>
                </div>
                <div className="dashboard-row">
                  <span className="dashboard-label">Company Name:</span>
                  <span className="dashboard-value">{approvedUser.company_name.split('-').join(' ')}</span>
                </div>
                <div className="dashboard-row">
                  <span className="dashboard-label">Account Email:</span>
                  <span className="dashboard-value">{approvedUser.account_email}</span>
                </div>
                <div className="dashboard-row">
                  <span className="dashboard-label">Account Status:</span>
                {(() => {
                  const status = (approvedUser.request_status || approvedUser.status || "").toUpperCase();
                  const isActive = status === "COMPLETED";
                  // fleet_disabled_at is set while workstation access is suspended for a billing
                  // issue and cleared when a payment succeeds; the reason code stays internal.
                  const isDisabled = isActive && !!approvedUser.fleet_disabled_at;
                  const statusColor = isDisabled ? "#dc3545" : isActive ? "#4caf50" : "#6c757d";
                  const statusLabel = isDisabled ? "Disabled" : isActive ? "Active" : "Inactive";

                  return (
                    <span style={{ textAlign: "right" }}>
                      <span
                        className="dashboard-status"
                        style={{
                          color: statusColor,
                        }}
                      >
                        {statusLabel}
                      </span>
                      {isDisabled && (
                        <span style={{ display: "block", marginTop: "4px", fontSize: "12px", color: "#dc3545" }}>
                          Billing issue: update your payment method below to restore access.
                        </span>
                      )}
                    </span>
                  );
                })()}
              </div>
            </div>

              {/* Close Account Button */}
              {(() => {
                  const status = (approvedUser.request_status || approvedUser.status || "").toUpperCase();
                  const isClosable = (status === "COMPLETED" || status.includes("FAILED")) && approvedUser.account_number;
                  
                  return (
                    <div style={{ marginTop: "30px", padding: "20px", backgroundColor: "#fff3cd", borderRadius: "8px", border: "1px solid #ffc107", opacity: isClosable ? 1 : 0.6 }}>
                      <h3 style={{ color: "#856404", marginTop: 0 }}>Close Account</h3>
                      <p style={{ color: "#856404", marginBottom: "15px" }}>
                        {isClosable 
                          ? "Permanently close your account and delete all associated data and infrastructure." 
                          : "Account closure is unavailable while the account is provisioning or missing an account number."}
                      </p>
                      <button
                        onClick={handleCloseAccount}
                        disabled={!isClosable}
                        style={{
                          backgroundColor: isClosable ? "#dc3545" : "#6c757d",
                          color: "white",
                          padding: "10px 20px",
                          border: "none",
                          borderRadius: "4px",
                          fontSize: "14px",
                          fontWeight: "500",
                          cursor: isClosable ? "pointer" : "not-allowed",
                          transition: "background-color 0.2s"
                        }}
                        onMouseOver={(e) => { if (isClosable) e.target.style.backgroundColor = "#c82333" }}
                        onMouseOut={(e) => { if (isClosable) e.target.style.backgroundColor = "#dc3545" }}
                      >
                        Close Account
                      </button>
                    </div>
                  );
              })()}

              {/* Modify Users — navigates to AccountStatus for the full two-step payment flow */}
              {(() => {
                const status = (approvedUser.request_status || approvedUser.status || '').toUpperCase();
                if (status !== 'COMPLETED') return null;
                return (
                  <div style={{ marginTop: '16px', padding: '20px', backgroundColor: '#e8f4fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
                    <h3 style={{ color: '#1565c0', marginTop: 0, marginBottom: '8px' }}>Modify Users</h3>
                    <p style={{ color: '#1565c0', marginBottom: '12px', fontSize: '14px' }}>
                      Adjust the number of concurrent users for your fleet.
                    </p>
                    <button
                      onClick={() => navigateToStatus(approvedUser, 'modify')}
                      style={{ backgroundColor: '#1976d2', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
                    >
                      Modify Users
                    </button>
                  </div>
                );
              })()}

              {/* Update Payment Method — navigates to AccountStatus for the SetupIntent flow */}
              {(() => {
                const status = (approvedUser.request_status || approvedUser.status || '').toUpperCase();
                if (status !== 'COMPLETED') return null;
                return (
                  <div style={{ marginTop: '16px', padding: '20px', backgroundColor: '#e8f4fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
                    <h3 style={{ color: '#1565c0', marginTop: 0, marginBottom: '8px' }}>Update Payment Method</h3>
                    <p style={{ color: '#1565c0', marginBottom: '12px', fontSize: '14px' }}>
                      Save a new default card for future charges.
                    </p>
                    <button
                      onClick={() => navigateToStatus(approvedUser, 'update-payment')}
                      style={{ backgroundColor: '#1976d2', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
                    >
                      Update Payment Method
                    </button>
                  </div>
                );
              })()}
            </div>
          ) : (!isAdmin && failedAccount && !activeCreationRequest && !activeClosureRequest && !closedAccount) ? (
            <div className="dashboard-container">
              <h2>Dashboard</h2>
              <div className="dashboard-info">
                <div className="dashboard-row">
                  <span className="dashboard-label">Company Name:</span>
                  <span className="dashboard-value">{(failedAccount.company_name || '').split('-').join(' ')}</span>
                </div>
                <div className="dashboard-row">
                  <span className="dashboard-label">Account Email:</span>
                  <span className="dashboard-value">{failedAccount.account_email}</span>
                </div>
                <div className="dashboard-row">
                  <span className="dashboard-label">Account Status:</span>
                  <span className="dashboard-status" style={{ color: '#dc3545' }}>
                    Failed
                  </span>
                </div>
                {failedAccount.updated_at && (
                  <div className="dashboard-row">
                    <span className="dashboard-label">Last Updated:</span>
                    <span className="dashboard-value">{new Date(failedAccount.updated_at).toLocaleString()}</span>
                  </div>
                )}
                {failedAccount.failure_reason && (
                  <div className="dashboard-row">
                    <span className="dashboard-label">Reason:</span>
                    <span className="dashboard-value">{failedAccount.failure_reason}</span>
                  </div>
                )}
              </div>

              <div style={{ marginTop: "20px", padding: "16px", backgroundColor: "#f8d7da", borderRadius: "8px", border: "1px solid #f5c2c7" }}>
                <p style={{ margin: 0, color: "#842029" }}>
                  {failedStatusMessage}
                </p>
                <p style={{ margin: "12px 0 0", color: "#842029" }}>
                  You can submit a new account request from this dashboard.
                </p>
              </div>

              <div style={{ marginTop: "20px" }}>
                {!showFailedAccountCreateForm ? (
                  <button
                    onClick={() => setShowFailedAccountCreateForm(true)}
                    style={{
                      backgroundColor: "#1976d2",
                      color: "white",
                      padding: "10px 20px",
                      border: "none",
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "background-color 0.2s"
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = "#1565c0"}
                    onMouseOut={(e) => e.target.style.backgroundColor = "#1976d2"}
                  >
                    Create New Account
                  </button>
                ) : (
                  <CreateAccountForm
                    loggedInUser={loggedInUser}
                    isAdmin={isAdmin}
                    accountList={accounts}
                    pinnedNamePrefix={failedAccount?.name_prefix || ''}
                    fetchAccountStatusList={fetchAccountStatusList}
                  />
                )}
              </div>
            </div>
          ) : (!isAdmin && (closedAccount || activeClosureRequest) && !activeCreationRequest) ? ((() => {
            const dashboardAccount = activeClosureRequest || closedAccount;
            const isClosing = !!activeClosureRequest;
            const statusLabel = isClosing ? "Closing" : "Closed";

            return (
            <div className="dashboard-container">
              <h2>Dashboard</h2>
              <div className="dashboard-info">
                <div className="dashboard-row">
                  <span className="dashboard-label">Company Name:</span>
                  <span className="dashboard-value">{(dashboardAccount.company_name || '').split('-').join(' ')}</span>
                </div>
                <div className="dashboard-row">
                  <span className="dashboard-label">Account Email:</span>
                  <span className="dashboard-value">{dashboardAccount.account_email}</span>
                </div>
                <div className="dashboard-row">
                  <span className="dashboard-label">Account Status:</span>
                  <span className="dashboard-status" style={{ color: '#6c757d' }}>
                    {statusLabel}
                  </span>
                </div>
                {dashboardAccount.closed_at && (
                  <div className="dashboard-row">
                    <span className="dashboard-label">Closed At:</span>
                    <span className="dashboard-value">{new Date(dashboardAccount.closed_at).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {!isClosing && (
                <div style={{ marginTop: "20px", padding: "16px", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
                  <p style={{ margin: 0, color: "#495057" }}>
                    This account is closed. If you need to create a new account, please sign out and register for a new account. We recommend using a different email address if possible to avoid duplicate email errors.
                  </p>
                  <div style={{ marginTop: "15px" }}>
                    <button
                      onClick={() => navigateToClosureStatus(dashboardAccount)}
                      style={{
                        backgroundColor: "transparent",
                        color: "#1976d2",
                        padding: "8px 0",
                        border: "none",
                        fontSize: "14px",
                        fontWeight: "500",
                        cursor: "pointer",
                        textDecoration: "underline"
                      }}
                    >
                      View Account Closure Activity Status & Archive
                    </button>
                  </div>
                </div>
              )}
            </div>
            );
            })()) : (
            // All users (admin and regular): Only show request form, unless there's an active request
            // After submission, redirect to Account Activities page for tracking
            !activeCreationRequest && (
              <div>
                <CreateAccountForm
                  loggedInUser={loggedInUser}
                  isAdmin={isAdmin}
                  accountList={accounts}
                  pinnedNamePrefix={failedAccount?.name_prefix || closedAccount?.name_prefix || approvedUser?.name_prefix || ''}
                  fetchAccountStatusList={fetchAccountStatusList}
                />
                <div className="after-submission-note">
                  <p className="after-submission-note__body">
                    Once you submit, you'll be redirected to the Account Activities page to track progress in real-time — including creation and closure workflows.
                  </p>
                </div>
              </div>
            )
          )}
        </>
      )}

      {/* Close Account Modal */}
      {!isAdmin && (approvedUser || activeClosureRequest || closedAccount) && (
        <CloseAccountModal
          isOpen={showCloseModal}
          onClose={() => setShowCloseModal(false)}
          accountData={{
            ...approvedUser,
            requester_email: loggedInUser?.attributes?.email || approvedUser.requester_email
          }}
          onClosureComplete={handleClosureComplete}
        />
      )}
    </div>
  );
};

export default Accounts;
